import API from "api/client";
import { ShareableArtifactTypes } from "consts";
import { envConfig } from "config";
const { shareableArtifactsAPI } = envConfig;

/**
 * @typedef {object} ShareableItem
 * @property {number} id - userId, accountId, usergroupId
 * @property {'write'|'read'|'none'} permission
 */

/**
 * @typedef {object} SharingRequest
 * @property {ShareableItem[]} accounts
 * @property {ShareableItem[]} groups
 * @property {ShareableItem[]} users
 * @property {boolean} notification
 */

/**
 *
 * @param {import('types').ArtifactType} type
 * @param {string} uuid dashboard uuid
 * @returns {Promise<{}>}
 */
const getShareableArtifacts = (type, uuid) => {
  return API.get(`${shareableArtifactsAPI}${type}/${uuid}`).then((res) => res.data);
};

/**
 *
 * @param {import('types').ArtifactType} type
 * @param {string} uuid
 * @param {SharingRequest} obj
 * @returns {Promise<{}>}
 */
const updateShareableArtifacts = (type, uuid, obj) => {
  return API.put(`${shareableArtifactsAPI}${type}/${uuid}`, obj).then((res) => res.data);
};

/**
 *
 * @param {string|null} uuid
 * @returns {Promise<{}>}
 */
const getSegmentSharing = (uuid) => getShareableArtifacts(ShareableArtifactTypes.SEGMENT, uuid);

/**
 *
 * @param {string|null} uuid
 * @param {object} obj
 * @returns {Promise<{}>}
 */
const updateSegmentSharing = (uuid, obj) => updateShareableArtifacts(ShareableArtifactTypes.SEGMENT, uuid, obj);

/**
 *
 * @param {string} uuid dashboard uuid
 * @returns {Promise<{}>}
 */
const getDashboardSharing = (uuid) => getShareableArtifacts(ShareableArtifactTypes.DASHBOARD, uuid);

/**
 *
 * @param {string} uuid
 * @param {SharingRequest} obj
 * @returns {Promise<{}>}
 */
const updateDashboardSharing = (uuid, obj) => updateShareableArtifacts(ShareableArtifactTypes.DASHBOARD, uuid, obj);

/**
 *
 * @param {string} uuid
 * @returns {Promise<T>}
 */
const getDeepProfileSharing = (uuid) => getShareableArtifacts(ShareableArtifactTypes.DEEP_PROFILE, uuid);

/**
 *
 * @param {string} uuid
 * @param {object} obj
 * @returns {Promise<{}>}
 */
const updateDeepProfileSharing = (uuid, obj) =>
  updateShareableArtifacts(ShareableArtifactTypes.DEEP_PROFILE, uuid, obj);

/**
 *
 * @param {string | number} id
 * @returns {Promise<T>}
 */
const getMultiScoreSharing = (id) => getShareableArtifacts(ShareableArtifactTypes.COMPOUND_SCORE, id);

/**
 *
 * @param {string | number} id
 * @param {object} obj
 * @returns {Promise<{}>}
 */
const updateMultiScoreSharing = (id, obj) => updateShareableArtifacts(ShareableArtifactTypes.COMPOUND_SCORE, id, obj);

/**
 *
 * @param {string} uuid
 * @returns {Promise<T>}
 */
const getMultiTimeviewSharing = (uuid) => getShareableArtifacts(ShareableArtifactTypes.TIMEVIEW, uuid);

/**
 *
 * @param {string} uuid
 * @param {object} obj
 * @returns {Promise<{}>}
 */
const updateMultiTimeviewSharing = (uuid, obj) => updateShareableArtifacts(ShareableArtifactTypes.TIMEVIEW, uuid, obj);

/**
 *
 * @param {string} uuid
 * @returns {Promise<{}>}
 */
const getQuestionSharing = (uuid) => getShareableArtifacts(ShareableArtifactTypes.QUESTION, uuid);

/**
 *
 * @param {number|string} artifactRefid
 * @returns {Promise<{}>}
 */
const getSharedAccountsForQuestion = (artifactRefid) => {
  return API.get(`${shareableArtifactsAPI}questions/${artifactRefid}/accounts`).then((res) => res.data);
};

/**
 * @param artifactRefid {string|int}
 * @param req {array}
 * @returns {Promise<{}>}
 */
const createSharedAccountsForQuestion = (artifactRefid, req) =>
  API.post(`${shareableArtifactsAPI}questions/${artifactRefid}/accounts`, req).then((res) => res.data);

/**
 * @param artifactRefid {int|string}
 * @param accountId {int|string}
 * @returns {Promise<{}>}
 */
const deleteSharedAccountForQuestion = (artifactRefid, accountId) =>
  API.delete(`${shareableArtifactsAPI}questions/${artifactRefid}/accounts/${accountId}`).then((res) => res.data);

export default {
  get: getShareableArtifacts,
  getQuestionAccounts: getSharedAccountsForQuestion,
  createQuestionAccounts: createSharedAccountsForQuestion,
  dashboard: getDashboardSharing,
  segment: getSegmentSharing,
  deepProfile: getDeepProfileSharing,
  multiScore: getMultiScoreSharing,
  multiTimeview: getMultiTimeviewSharing,
  question: getQuestionSharing,
  update: updateShareableArtifacts,
  updateSegment: updateSegmentSharing,
  updateDashboard: updateDashboardSharing,
  updateDeepProfile: updateDeepProfileSharing,
  updateMultiScore: updateMultiScoreSharing,
  updateMultiTimeview: updateMultiTimeviewSharing,
  deleteQuestionAccount: deleteSharedAccountForQuestion,
};
